import { LocationStrategy } from '@angular/common';
import { computed, inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { filter } from 'rxjs';

type StaticUrlElement = 'protocol' | 'hostname' | 'port' | 'context' | 'module';
type DynamicUrlElement = 'route' | 'query' | 'fragment';
type UrlElement = StaticUrlElement | DynamicUrlElement;

/**
 * Service to interact with the activated route.
 * 
 * Provides additonal functionality to keep the components cleaner and access rout info in a safe way.
 */
@Injectable({providedIn: 'root'})
export class HisRouteService {
    private readonly router = inject( Router );
    private readonly locationStrategy = inject( LocationStrategy );

    /** Signal of the currently activated route. Automatically updates after navigation ends. */
    public readonly activatedRoute: Signal<ActivatedRoute>;
    /** Signal of the current query parameters. Always up to date with the current route state. */
    public readonly queryParams: Signal<Params>;

    private readonly applicationLocation = this.resolveApplicationLocation();

    constructor() {
        // Create a signal that updates on navigation end events
        const navigationTrigger = toSignal(
            this.router.events.pipe(
                filter( e => e instanceof NavigationEnd )
            ), { initialValue: null }
        );

        this.activatedRoute = computed( () => {
            // triggers signal recomputation on next access after navigation ends
            navigationTrigger();
            return this.queryCurrentActivatedRoute();
        } );

        this.queryParams = computed( () => {
            navigationTrigger(); // depends on the trigger itself, since the route object can stay identical
            return this.queryCurrentActivatedRoute().snapshot.queryParams;
        } );
    }

    /**
     * Standard getter for the activated route. Allows replacement of the explicit 'route' injection in components.
     * @returns `ActivatedRoute` instance
     */
    public getActivatedRoute(): ActivatedRoute {
        return this.queryCurrentActivatedRoute(); // live read, so components created during navigation get the new route
    }

    /**
     * Retrieves information from the activated route's data object.
     * @param prop the property key to query
     * @returns the value associated with the specified property key in the route data
     */
    public getActivatedRouteData( prop: string | symbol ): any {
        return this.getActivatedRoute().snapshot.data[prop];
    }

    /**
     * Extracts a complete path of the current route. The path starts after the domain (and partial path where the application is served)
     * and ends with the last path segment of the current route.
     * 
     * For a URL `https://his.de/hisinone/a/cs.sys.hing/home/dashboard`, the angular application is served at `https://his.de/hisinone/a/cs.sys.hing`,
     * leaving `/home/dashboard` as the remaining route path.
     * 
     * The returned path is a template, meaning that route parameters are represented as `:paramName` (identical to the route config path), not their actual value in the URL.
     * @returns the full path template of the current route (rooted at `/`, never empty)
     */
    public getRoutePathTemplate(): string {
        let path = '';
        this.getActivatedRoute().pathFromRoot.forEach( r => {
            if ( r.snapshot.routeConfig?.path?.length > 0 ) {
                path += '/' + r.snapshot.routeConfig.path;
            }
        } );
        return path || '/';
    }

    /**
     * Extracts a complete path of the current route. The path starts after the domain (and partial path where the application is served)
     * and ends with the last path segment of the current route.
     * 
     * For a URL `https://his.de/hisinone/a/cs.sys.hing/home/dashboard`, the angular application is served at `https://his.de/hisinone/a/cs.sys.hing`,
     * leaving `/home/dashboard` as the remaining route path.
     * 
     * The returned path is absolute, meaning that route parameters are represented as their actual value in the URL, not their parameter name.
     * @returns the full path of the current route (rooted at `/`, never empty)
     */
    public getRoutePathAbsolute(): string {
        let path = '';
        this.getActivatedRoute().pathFromRoot.forEach( r => {
            if ( r.snapshot.url.length > 0 ) {
                path += '/' + r.snapshot.url.map( segment => segment.path ).join('/');
            }
        } );
        return path || '/';
    }

    /**
     * Retrieves a specific element of the current URL, such as the host, context, module, route, query, or fragment.
     * ```
     * 'http     :// muster-head.de : 8080 /qisserver /a/ cs.sys.hisng /route/path/1234 ?query=example #fragment'
     *  protocol     hostname         port context        module       route            query          fragment
     * ```
     * Not all elements may always be present in the URL, they return empty strings if absent.
     * @param element The specific part of the URL to retrieve (e.g., 'host', 'context', 'module', 'route', 'query', 'fragment').
     * @returns The value of the specified URL element as a string.
     */
    public getUrlElement( element: UrlElement ): string {
        if ( element === 'route' || element === 'query' || element === 'fragment' ) {
            return this.resolveDynamicUrlElements()[element];
        }
        return this.applicationLocation[element];
    }

    /**
     * Retrieves all elements of the current URL, such as the host, context, module, route, query, or fragment.
     * ```
     * 'http     :// muster-head.de : 8080 /qisserver /a/ cs.sys.hisng /route/path/1234 ?query=example #fragment'
     *  protocol     hostname         port context        module       route            query          fragment
     * ```
     * Not all elements may always be present in the URL, they return empty strings if absent.
     * @returns An object containing all URL elements as strings.
     */
    public getUrlElements(): Record<UrlElement, string> {
        return {
            ...this.applicationLocation,
            ...this.resolveDynamicUrlElements()
        };
    }

    private resolveDynamicUrlElements(): Record<DynamicUrlElement, string> {
        const routeUrl = new URL( this.router.url, window.location.origin ); // => muster-head.de/route/path/1234
        return {
            route: routeUrl.pathname,
            query: routeUrl.search,
            fragment: routeUrl.hash
        };
    }

    private resolveApplicationLocation(): Record<StaticUrlElement, string> {
        const { protocol, hostname, port, origin } = window.location;
        const baseSegments = new URL( this.locationStrategy.getBaseHref(), origin )
            .pathname.split( '/' ).filter( Boolean );
        const markerIndex = baseSegments.indexOf( 'a' ); // Angular context marker

        return {
            protocol,
            hostname,
            port,
            context: markerIndex > 0 ? '/' + baseSegments.slice( 0, markerIndex ).join( '/' ) : '',
            module: markerIndex < 0 ? '' : baseSegments[markerIndex + 1] ?? ''
        };
    }

    /**
     * Reads the live router state to find the currently active route (deepest child).
     * @returns the current `ActivatedRoute`
     */
    private queryCurrentActivatedRoute(): ActivatedRoute {
        let route = this.router.routerState.root;
        while ( route.firstChild ) {
            route = route.firstChild;
        }
        return route;
    }

    /**
     * clean up parameter suffixes.
     *
     * Workaround for https://github.com/angular/angular/issues/14137
     */
    cleanupParamSuffixes( params: Params ): Params {
        let res: Params = {};
        for ( let key in params ) {
            if ( key.indexOf( '{' ) === 0 ) {
                let pos = key.indexOf( '}' );
                let suffixLength = key.length - pos - 1;
                let realKey = key.substring( 1, pos );
                let realValue = params[key].substring( 0, params[key].length - suffixLength );
                res[realKey] = realValue;
            } else {
                res[key] = params[key];
            }
        }
        return res;
    }
}
